import { FormEvent, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { I18N_SUPPORTED, useI18n, type SupportedLocale } from '@runtime/core/i18n/i18n'
import { useAppStore } from '@runtime/core/store/appStore'
import { getPosterThemeAccessTokens } from '@runtime/core/theme/posterAccess'
import { CoachRequestError, streamCoach } from './coachClient'
import { isSameCoachBoardFocus } from './coachBoardFocus'
import { coachErrorMessage } from './coachErrorMessage'
import { useCoach } from './CoachContext'
import { useFloatingCoachWindow } from './useFloatingCoachWindow'
import type { CoachMessage } from './coach.types'
import styles from './CoachPanel.module.scss'
import CoachRichText from './CoachRichText'
import CoachPosterRecommendationCard from './CoachPosterRecommendationCard'
import CoachLauncher from './CoachLauncher'
import CoachLocalActions from './CoachLocalActions'
import CoachResetPosition from './CoachResetPosition'
import { useCoachLauncherPlacement } from './CoachLauncherPlacement'
import { useCoachInputVisibility } from './useCoachInputVisibility'
import { getHelpCharacter } from '@/features/help/characters/helpCharacters'

const ALLOWED_ROUTES = new Set([
  '/',
  '/home',
  '/hub',
  '/strategy',
  '/rules',
  '/challenges',
  '/online',
  '/ranking',
  '/tools',
])

function messageId(role: CoachMessage['role']) {
  return `${role}-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export default function CoachPanel() {
  const { t, locale } = useI18n()
  const authToken = useAppStore(state => state.authToken)
  const setLoginOpen = useAppStore(state => state.setLoginOpen)
  const navigate = useNavigate()
  const {
    isOpen,
    setOpen,
    messages,
    setMessages,
    gameContext,
    prepareGameContext,
    pinnedBoardFocus,
    previewBoardFocus,
    toggleBoardFocus,
    clearBoardFocus,
    route,
    screen,
  } = useCoach()
  const [question, setQuestion] = useState('')
  const [isLoading, setLoading] = useState(false)
  const [hasStartedResponse, setHasStartedResponse] = useState(false)
  const [streamingMessageId, setStreamingMessageId] = useState<string>()
  const inputRef = useRef<HTMLInputElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const requestAbortRef = useRef<AbortController | null>(null)
  const coachCharacter = getHelpCharacter('coach')
  const { dragHandleProps, isDragging, panelRef, headerRef, panelStyle } =
    useFloatingCoachWindow(isOpen)
  const placement = useCoachLauncherPlacement()
  useCoachInputVisibility(isOpen, bodyRef, inputRef, formRef)

  useEffect(() => {
    if (!isOpen) return
    closeRef.current?.focus({ preventScroll: true })
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) return
      // Keyboard closure returns to the launcher even if focus left this non-modal panel.
      event.preventDefault()
      if (!panelRef.current?.contains(document.activeElement))
        closeRef.current?.focus({ preventScroll: true })
      setOpen(false)
    }
    window.addEventListener('keydown', onEscape)
    return () => window.removeEventListener('keydown', onEscape)
  }, [isOpen, panelRef, setOpen])

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight })
  }, [isLoading, messages])

  useEffect(() => () => requestAbortRef.current?.abort(), [])

  const submit = async (event?: FormEvent, suggestion?: string) => {
    event?.preventDefault()
    const content = (suggestion ?? question).trim()
    if (!content || isLoading) return
    clearBoardFocus()

    const userMessage: CoachMessage = { id: messageId('user'), role: 'user', content }
    const assistantId = messageId('assistant')
    const assistantMessage: CoachMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
    }
    const previousMessages = messages
    setMessages(current => [...current, userMessage, assistantMessage])
    setQuestion('')
    setHasStartedResponse(false)
    setStreamingMessageId(assistantId)
    setLoading(true)
    const abortController = new AbortController()
    requestAbortRef.current = abortController
    try {
      let preparedGameContext = gameContext
      if (prepareGameContext) {
        try {
          preparedGameContext = await prepareGameContext()
        } catch {
          // The lightweight context remains sufficient if local engine analysis fails.
        }
      }
      await streamCoach(
        {
          question: content,
          locale: I18N_SUPPORTED.includes(locale as SupportedLocale)
            ? (locale as SupportedLocale)
            : 'fr',
          route,
          screen,
          game: preparedGameContext,
          messages: previousMessages,
          authToken,
          posterAccessTokens: getPosterThemeAccessTokens().slice(-40),
        },
        event => {
          if (event.type === 'text-delta') {
            setHasStartedResponse(true)
            setMessages(current =>
              current.map(message =>
                message.id === assistantId
                  ? { ...message, content: message.content + event.delta }
                  : message,
              ),
            )
            return
          }
          if (event.type !== 'done') return

          setMessages(current =>
            current.map(message =>
              message.id === assistantId
                ? {
                    ...message,
                    content: event.response.message,
                    actions: event.response.actions,
                    posterRecommendations: event.response.posterRecommendations,
                    failed: false,
                  }
                : message,
            ),
          )
        },
        abortController.signal,
      )
    } catch (error) {
      setMessages(current =>
        current.map(message => {
          if (message.id === userMessage.id) return { ...message, failed: true }
          return message.id === assistantId
            ? {
                ...message,
                content: coachErrorMessage(
                  error,
                  t,
                  I18N_SUPPORTED.includes(locale as SupportedLocale)
                    ? (locale as SupportedLocale)
                    : 'fr',
                ),
                actions: undefined,
                posterRecommendations: undefined,
                canLogin:
                  !authToken && error instanceof CoachRequestError && error.canLogin === true,
                failed: true,
              }
            : message
        }),
      )
    } finally {
      requestAbortRef.current = null
      setStreamingMessageId(undefined)
      setLoading(false)
    }
  }

  const suggestions = [
    t('coach.suggestions.findMode'),
    t('coach.suggestions.choosePoster'),
    t('coach.suggestions.explainRule'),
  ]
  if (screen === 'strategy' && !gameContext?.online) {
    suggestions.push(t('coach.suggestions.strategy'))
  }

  const launcher = <CoachLauncher isOpen={isOpen} onToggle={() => setOpen(!isOpen)} />

  return (
    <>
      {!placement?.isPending &&
        (placement?.host ? createPortal(launcher, placement.host) : launcher)}

      {isOpen && (
        <aside
          id="coach-panel"
          ref={panelRef}
          className={styles.panel}
          style={panelStyle}
          aria-label={t('coach.title')}
          data-dragging={isDragging}
          data-testid="coach-panel"
        >
          <header ref={headerRef} className={styles.header} data-testid="coach-header">
            <button
              className={styles.dragHandle}
              type="button"
              aria-label={t('coach.moveWindow')}
              {...dragHandleProps}
            >
              <span className={styles.identity}>
                <span className={styles.gameTile} aria-hidden="true" />
                <span>
                  <strong>{t('coach.title')}</strong>
                  <span className={styles.subtitle}>{t('coach.subtitle')}</span>
                </span>
              </span>
              <span className={styles.dragIndicator} aria-hidden="true" />
            </button>
            <button
              ref={closeRef}
              className={styles.closeButton}
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t('coach.close')}
              data-testid="coach-close"
            >
              ×
            </button>
          </header>

          <CoachLocalActions />

          <div className={styles.body} ref={bodyRef} data-testid="coach-body">
            <CoachResetPosition />
            <div className={styles.transcript} aria-live="polite">
              {messages.length === 0 && (
                <section className={styles.welcome}>
                  <div className={styles.welcomeBubble}>
                    <span aria-hidden="true">◆</span>
                    <p>{t('coach.welcome')}</p>
                  </div>
                  <div className={styles.coachPortrait} aria-hidden="true">
                    <img src={coachCharacter.img} alt="" />
                  </div>
                </section>
              )}
              {messages
                .filter(message => message.content)
                .map(message => (
                  <article key={message.id} className={styles[message.role]}>
                    {message.role === 'assistant' && (
                      <span className={styles.messageTile} aria-hidden="true" />
                    )}
                    <div className={styles.messageBubble}>
                      <span>{message.role === 'user' ? t('coach.you') : t('coach.title')}</span>
                      <p
                        className={
                          message.id === streamingMessageId ? styles.streamingText : undefined
                        }
                      >
                        <CoachRichText
                          content={message.content}
                          tileLabel={(tile, rotation) =>
                            rotation === undefined
                              ? t('coach.tileAlt', { tile: tile.toUpperCase() })
                              : t('coach.tileAltWithRotation', {
                                  tile: tile.toUpperCase(),
                                  rotation,
                                })
                          }
                          cellReference={{
                            label: cellNumber => t('coach.cellLabel', { cell: cellNumber }),
                            actionLabel: cellNumber => t('coach.cellAction', { cell: cellNumber }),
                            isPinned: focus => isSameCoachBoardFocus(pinnedBoardFocus, focus),
                            onPreview: previewBoardFocus,
                            onPreviewEnd: () => previewBoardFocus(undefined),
                            onActivate: focus => {
                              toggleBoardFocus(focus)
                              const cell = document.querySelector(
                                `[data-coach-cell-number="${focus.cellNumber}"]`,
                              )
                              const reduceMotion = window.matchMedia(
                                '(prefers-reduced-motion: reduce)',
                              ).matches
                              cell?.scrollIntoView({
                                behavior: reduceMotion ? 'auto' : 'smooth',
                                block: 'center',
                                inline: 'center',
                              })
                              if (window.matchMedia('(max-width: 520px)').matches) {
                                setOpen(false)
                              }
                            },
                          }}
                        />
                      </p>
                      {message.posterRecommendations?.map(recommendation => (
                        <CoachPosterRecommendationCard
                          key={recommendation.themeId}
                          recommendation={recommendation}
                          onApplied={() => {
                            if (window.matchMedia('(max-width: 520px)').matches) {
                              setOpen(false)
                            }
                          }}
                        />
                      ))}
                      {!!message.actions?.length && (
                        <div className={styles.actions}>
                          {message.actions
                            .filter(action => ALLOWED_ROUTES.has(action.route))
                            .map(action => (
                              <button
                                type="button"
                                key={`${action.route}-${action.label}`}
                                onClick={() =>
                                  navigate(action.route === '/' ? '/home' : action.route)
                                }
                              >
                                <span aria-hidden="true">◆</span>
                                {action.label}
                              </button>
                            ))}
                        </div>
                      )}
                      {message.canLogin && (
                        <button
                          type="button"
                          className={styles.loginAction}
                          onClick={() => setLoginOpen(true)}
                        >
                          {t('coach.quota.login')}
                        </button>
                      )}
                    </div>
                  </article>
                ))}
              {isLoading && !hasStartedResponse && (
                <div className={styles.loading}>
                  <span className={styles.messageTile} aria-hidden="true" />
                  <p>
                    {t('coach.thinking')}
                    <span className={styles.loadingDots} aria-hidden="true">
                      <i />
                      <i />
                      <i />
                    </span>
                  </p>
                </div>
              )}
            </div>

            {messages.length === 0 && (
              <section className={styles.suggestionArea} aria-label={t('coach.suggestionsLabel')}>
                <p className={styles.suggestionTitle}>
                  <span aria-hidden="true">✦</span>
                  {t('coach.suggestionsLabel')}
                </p>
                <div className={styles.suggestions}>
                  {suggestions.map((suggestion, index) => (
                    <button
                      type="button"
                      key={suggestion}
                      onClick={() => submit(undefined, suggestion)}
                    >
                      <span aria-hidden="true">{index + 1}</span>
                      {suggestion}
                    </button>
                  ))}
                </div>
              </section>
            )}

            <form ref={formRef} className={styles.form} onSubmit={submit}>
              <label htmlFor="coach-question">{t('coach.inputLabel')}</label>
              <div>
                <input
                  id="coach-question"
                  ref={inputRef}
                  value={question}
                  onChange={event => setQuestion(event.target.value)}
                  placeholder={t('coach.placeholder')}
                  maxLength={1200}
                  disabled={isLoading}
                />
                <button type="submit" disabled={isLoading || !question.trim()}>
                  <span>{t('coach.send')}</span>
                  <span aria-hidden="true">→</span>
                </button>
              </div>
            </form>
            <p className={styles.privacy}>{t('coach.privacy')}</p>
          </div>
        </aside>
      )}
    </>
  )
}
